import React, { useEffect, useState } from 'react';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';

import { Collapse, Row, Col, Card, Spin, Typography, Badge } from 'antd';

import { EyeInvisibleOutlined } from '@ant-design/icons';

import OverviewBucket from './overview-bucket';
import OverviewHeader from './overview-header';

import { currencyFormatter } from '../../../shared/utils';
import { pipelinePagesTypes } from '../../../shared/constants/enums/pipelinePagesTypes';
import '../styles.less';

const { Panel } = Collapse;

const Overview = ({
  columns,
  renderBucketItem,
  type,
  userInfo,
  isLoading,
  refresh,
  changeStatus,
  showTotals,
  showModal,
  loadSingleBucketData,
  rootContainer,
  configuratorEnabled,
  saveControlConfiguration,
  updateColumnsState,
  fetchData,
  wonLostColumns,
  linkToList,
  setIncludeRenewal,
  onPipelineTypeChange,
  refreshConstrolConfiguration,
  isMomentumEnabled,
  canViewOnlyAssigned,
  statuses,
  customFiltersData,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [bucketSpan, setBucketSpan] = useState(null);

  useEffect(() => {
    setBucketSpan(calculateBucketSpan(columns || []));
  }, [columns, type]);

  const onDragEnd = (result) => {
    setIsDragging(false);

    if (!result.destination) return;
    const { source, destination } = result;

    if (source.droppableId !== destination.droppableId) {
      const sourceColumn = columns[source.droppableId];
      const sourceItems = [...sourceColumn.items];
      const [moved] = sourceItems.splice(source.index, 1);

      let destColumn = columns[destination.droppableId];

      let shouldNotUpdateDestColumn = false;

      if (!destColumn && !!wonLostColumns) {
        destColumn = wonLostColumns[destination.droppableId];
        shouldNotUpdateDestColumn = true;
      }

      if (destColumn.status || destColumn.stage || destColumn.customType || destColumn.status === 0) {
        if (!showModal) {
          changeStatus(sourceColumn, destColumn, moved, shouldNotUpdateDestColumn);
        } else {
          showModal(sourceColumn, destColumn, source, destination, moved);
        }
      }
    }
  };

  const onDragStart = (result) => {
    setIsDragging(!!result?.draggableId);
  };

  const rowStyles = () => {
    if (isLoading) {
      return {
        flexFlow: 'nowrap',
        minHeight: '50px',
        justifyContent: 'center',
        alignItems: 'center',
      };
    } else {
      return { flexFlow: 'nowrap' };
    }
  };

  const calculateBucketSpan = (cols) => {
    let len = 0;
    Object.keys(cols).forEach((key) => {
      if (key && cols[key] && cols[key].visible) {
        len++;
      }
    });

    // 24 is gutter value, 5 will be default col span
    const results = cols && len && len > 0 && len <= 8 ? Math.floor(24 / len) : 5;
    return results;
  };

  return (
    <>
      <Collapse collapsible={false} activeKey="1" className="overview-collapse-panel">
        <Panel
          showArrow={false}
          key="1"
          collapsible={false}
          forceRender={true}
          header={
            <OverviewHeader
              columns={columns}
              type={type}
              userInfo={userInfo}
              refresh={refresh}
              rootContainer={rootContainer}
              configuratorEnabled={configuratorEnabled}
              saveControlConfiguration={saveControlConfiguration}
              updateColumnsState={updateColumnsState}
              fetchData={fetchData}
              linkToList={linkToList}
              setIncludeRenewal={setIncludeRenewal}
              onPipelineTypeChange={onPipelineTypeChange}
              refreshConstrolConfiguration={refreshConstrolConfiguration}
              isLoading={isLoading}
              isMomentumEnabled={isMomentumEnabled}
              canViewOnlyAssigned={canViewOnlyAssigned}
              statuses={statuses}
              customFiltersData={customFiltersData}
            />
          }
        >
          <DragDropContext onDragStart={(result) => onDragStart(result)} onDragEnd={(result) => onDragEnd(result)}>
            <Row
              gutter={16}
              style={{
                flexFlow: 'nowrap',
                justifyContent: 'center',
                alignItems: 'center',
                margin: 0,
                marginBottom: isDragging ? '10px' : 0,
                visibility: isDragging ? 'visible' : 'hidden',
                height: isDragging ? 'auto' : '0',
                maxHeight: 'auto',
                transition: 'max-height 0.25s ease-in',
              }}
            >
              {!isLoading && wonLostColumns ? (
                Object.entries(wonLostColumns).map(([columnId, column], index) => {
                  return (
                    <Col span={12} key={columnId} style={index === 0 ? { paddingLeft: 0 } : { paddingRight: 0 }}>
                      <Droppable droppableId={columnId} key={columnId}>
                        {(provided, snapshot) => {
                          const dragOverClassName = index === 0 ? 'wonBucketDragging' : 'lostDragging';

                          return (
                            <div
                              {...provided.droppableProps}
                              ref={provided.innerRef}
                              className={`pipelines-drag-drop-container ${snapshot.isDraggingOver ? dragOverClassName : 'default'}`}
                              style={{ border: index === 0 ? '1px dashed green' : '1px dashed red', maxHeight: '44px' }}
                            >
                              <div style={{ textAlign: 'center' }}>Drag item here to mark as {column.name}</div>

                              {provided.placeholder}
                            </div>
                          );
                        }}
                      </Droppable>
                    </Col>
                  );
                })
              ) : (
                <></>
              )}
            </Row>

            {isLoading ? (
              <Spin />
            ) : columns ? (
              <Row gutter={16} style={rowStyles()}>
                {Object.entries(columns)
                  .sort(([, a], [, b]) => a.order - b.order)
                  .map(([columnId, column], index) => {
                    return column.visible ? (
                      <Col span={bucketSpan} key={columnId} style={{ minHeight: '82vh', paddingBottom: '10px' }}>
                        <Card
                          title={
                            <>
                              <div
                                style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', display: 'inline' }}
                                title={column.name}
                              >
                                {column.name}
                              </div>
                              {showTotals ? (
                                <div className="text-info" style={{ display: 'inline', float: 'inline-end' }}>
                                  {type === pipelinePagesTypes.TaskOverview && column?.totalCount ? (
                                    <Badge count={column.totalCount} color="purple" />
                                  ) : type === pipelinePagesTypes.OpportunitiesOverview || type === pipelinePagesTypes.RenewalOverview ? (
                                    <>
                                      ({column.items ? column.items.length : 0}) {currencyFormatter(column.total)}
                                    </>
                                  ) : null}
                                </div>
                              ) : null}
                            </>
                          }
                          size="small"
                          bodyStyle={{ padding: 0, height: showTotals ? 'calc(100% - 60px)' : 'calc(100% - 38px)' }}
                        >
                          <Droppable droppableId={columnId} key={columnId}>
                            {(provided, snapshot) => {
                              return (
                                <OverviewBucket
                                  isLoading={isLoading}
                                  provided={provided}
                                  snapshot={snapshot}
                                  column={column}
                                  renderBucketItem={renderBucketItem}
                                  loadSingleBucketData={loadSingleBucketData}
                                />
                              );
                            }}
                          </Droppable>
                        </Card>
                      </Col>
                    ) : null;
                  })}
              </Row>
            ) : (
              <div
                className="text-primary font-fixed-14"
                style={{ display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center', margin: 'auto' }}
              >
                <EyeInvisibleOutlined style={{ fontSize: 'x-large' }} />
                <Typography.Title level={4} type="secondary">
                  {' '}
                  No Data Available
                </Typography.Title>
              </div>
            )}
          </DragDropContext>
        </Panel>
      </Collapse>
    </>
  );
};

export default Overview;
